import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  Paper,
  Skeleton,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

export type HubPreviewTone = "error" | "warning" | "info" | "success" | "neutral";

export interface HubCountChip {
  label: string;
  value: number | string;
  tone?: HubPreviewTone;
}

export interface HubPreviewRow {
  id: string;
  title: string;
  subtitle?: string;
  meta?: string;
  tone?: HubPreviewTone;
  onClick?: () => void;
}

interface HubGroupPanelProps {
  icon: React.ReactNode;
  title: string;
  href: string;
  hrefLabel?: string;
  loading?: boolean;
  emptyText: string;
  counts: HubCountChip[];
  rows: HubPreviewRow[];
  previewCount?: number;
}

const CHIP_COLOR: Record<
  HubPreviewTone,
  "error" | "warning" | "info" | "success" | "default"
> = {
  error: "error",
  warning: "warning",
  info: "info",
  success: "success",
  neutral: "default",
};

const META_COLOR: Record<HubPreviewTone, string> = {
  error: "error.main",
  warning: "warning.main",
  info: "info.main",
  success: "success.main",
  neutral: "text.secondary",
};

const isChipActive = (count: HubCountChip) => {
  if (typeof count.value === "number") return count.value > 0;
  const trimmed = count.value.trim();
  return trimmed !== "" && trimmed !== "$0" && trimmed !== "0";
};

const HubGroupPanel: React.FC<HubGroupPanelProps> = ({
  icon,
  title,
  href,
  hrefLabel = "Open",
  loading = false,
  emptyText,
  counts,
  rows,
  previewCount = 3,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const hiddenCount = Math.max(0, rows.length - previewCount);
  const visibleRows = expanded ? rows : rows.slice(0, previewCount);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          px: 1.5,
          py: 1.1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              flexShrink: 0,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "action.hover",
              color: "text.secondary",
              "& .MuiSvgIcon-root": { fontSize: "1rem" },
            }}
          >
            {icon}
          </Box>
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: 600, fontSize: "0.8rem", minWidth: 0, flexGrow: 1 }}
          >
            {title}
          </Typography>
          <Button
            size="small"
            endIcon={<ArrowForwardIcon sx={{ fontSize: "0.85rem" }} />}
            onClick={() => navigate(href)}
            sx={{ flexShrink: 0, minWidth: 0, fontSize: "0.75rem" }}
          >
            {hrefLabel}
          </Button>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, pl: 4.5 }}>
          {loading
            ? [0, 1].map((key) => (
                <Skeleton key={key} width={72} height={22} />
              ))
            : counts.map((count) => (
                <Chip
                  key={count.label}
                  size="small"
                  variant="outlined"
                  color={
                    isChipActive(count)
                      ? CHIP_COLOR[count.tone ?? "neutral"]
                      : "default"
                  }
                  label={`${count.value} ${count.label}`}
                  sx={{
                    height: 22,
                    fontSize: "0.68rem",
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              ))}
        </Box>
      </Box>

      <Divider />

      {loading ? (
        <Box sx={{ px: 1.5, py: 1 }}>
          {[0, 1, 2].map((key) => (
            <Box key={key} sx={{ py: 0.75 }}>
              <Skeleton width="45%" height={14} />
              <Skeleton width="70%" height={14} />
            </Box>
          ))}
        </Box>
      ) : rows.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 1.5, py: 1.25, fontSize: "0.78rem" }}
        >
          {emptyText}
        </Typography>
      ) : (
        <>
          {visibleRows.map((row, index) => (
            <ButtonBase
              key={row.id}
              onClick={row.onClick}
              disabled={!row.onClick}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                width: "100%",
                textAlign: "left",
                px: 1.5,
                py: 0.95,
                borderTop: index === 0 ? "none" : "1px solid",
                borderColor: "divider",
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ fontWeight: 600, fontSize: "0.78rem", lineHeight: 1.3 }}
                >
                  {row.title}
                </Typography>
                {row.subtitle && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: "block", lineHeight: 1.3 }}
                  >
                    {row.subtitle}
                  </Typography>
                )}
              </Box>
              {row.meta && (
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    flexShrink: 0,
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    color: META_COLOR[row.tone ?? "neutral"],
                  }}
                >
                  {row.meta}
                </Typography>
              )}
              {row.onClick && (
                <ArrowForwardIcon
                  sx={{
                    fontSize: "0.85rem",
                    color: "text.disabled",
                    flexShrink: 0,
                  }}
                />
              )}
            </ButtonBase>
          ))}
          {hiddenCount > 0 && (
            <Box
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                px: 1.25,
                py: 0.35,
              }}
            >
              <Button
                size="small"
                onClick={() => setExpanded((open) => !open)}
                sx={{ fontSize: "0.72rem" }}
              >
                {expanded ? "Show less" : `Show ${hiddenCount} more`}
              </Button>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default HubGroupPanel;
